import React, { useState, useEffect, useCallback } from 'react'
import api from '../api/client'
import type { ShiftAssignment } from '../types'
import { X, Calendar, Clock, MapPin, Users } from 'lucide-react'
import { StatusBadge } from '@rallyshift/design-system'
import { formatDayDateToLocal, formatTimeRangeToLocal } from '../utils/timezone'
import { statusLabel } from '../utils/status'

interface VolunteerShiftsModalProps {
  isOpen: boolean
  onClose: () => void
  volunteer: {
    id: string
    first_name: string
    last_name: string
    email: string
    avatar_url?: string | null
  } | null
}

interface ShiftWithAssignment {
  id: string
  event_id?: string
  title: string
  description?: string
  start_time: string
  end_time: string
  location: string
  max_volunteers: number
  current_volunteers: number
  status: string
  shift_type?: string
  created_at: string
  updated_at: string
  assignment: ShiftAssignment
  event?: {
    title: string
  }
}

const VolunteerShiftsModal: React.FC<VolunteerShiftsModalProps> = ({
  isOpen,
  onClose,
  volunteer
}) => {
  const [shifts, setShifts] = useState<ShiftWithAssignment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const fetchVolunteerShifts = useCallback(async () => {
    if (!volunteer) return

    try {
      setLoading(true)
      setError('')
      const list = await api.shifts.getAssignmentsByVolunteer(volunteer.id)
      const shiftsWithAssignments: ShiftWithAssignment[] = (list || [])
        .filter((a: any) => a.status !== 'cancelled')
        .map((a: any) => ({
          id: a.shift?.id || a.shift_id,
          event_id: a.shift?.event_id,
          title: a.shift?.title || '',
          description: a.shift?.description,
          start_time: a.shift?.start_time,
          end_time: a.shift?.end_time,
          location: a.shift?.location || '',
          max_volunteers: a.shift?.max_volunteers ?? 0,
          current_volunteers: a.shift?.current_volunteers ?? 0,
          status: a.shift?.status || '',
          shift_type: a.shift?.shift_type,
          created_at: a.shift?.created_at || '',
          updated_at: a.shift?.updated_at || '',
          assignment: {
            id: a.id,
            shift_id: a.shift_id,
            volunteer_id: a.volunteer_id,
            status: a.status,
            assigned_at: a.assigned_at,
            confirmed_at: a.confirmed_at,
            completed_at: a.completed_at,
            notes: a.notes
          },
          event: a.shift?.event ? { title: a.shift.event.title } : undefined
        }))
      setShifts(shiftsWithAssignments)
    } catch (err: any) {
      console.error('Error fetching volunteer shifts:', err)
      setError(err.message || 'Failed to fetch volunteer shifts')
    } finally {
      setLoading(false)
    }
  }, [volunteer])

  useEffect(() => {
    if (isOpen && volunteer) {
      fetchVolunteerShifts()
    }
  }, [isOpen, volunteer, fetchVolunteerShifts])

  if (!isOpen || !volunteer) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              {volunteer.avatar_url ? (
                <img
                  className="h-12 w-12 rounded-full"
                  src={volunteer.avatar_url}
                  alt={`${volunteer.first_name} ${volunteer.last_name}`}
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-lg font-medium text-gray-700">
                    {volunteer.first_name[0]}{volunteer.last_name[0]}
                  </span>
                </div>
              )}
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Shifts van {volunteer.first_name} {volunteer.last_name}
                </h3>
                <p className="text-sm text-gray-500">{volunteer.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="ml-3 text-sm text-gray-500">Shifts laden...</p>
            </div>
          ) : shifts.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Geen shifts toegewezen</h3>
              <p className="mt-1 text-sm text-gray-500">
                Deze vrijwilliger heeft nog geen shifts toegewezen gekregen.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{shifts.length}</div>
                    <div className="text-sm text-gray-500">Totaal Shifts</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {shifts.filter(s => s.assignment.status === 'confirmed').length}
                    </div>
                    <div className="text-sm text-gray-500">Bevestigd</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {shifts.filter(s => s.assignment.status === 'completed').length}
                    </div>
                    <div className="text-sm text-gray-500">Voltooid</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {shifts.filter(s => s.assignment.status === 'assigned').length}
                    </div>
                    <div className="text-sm text-gray-500">Wachtend</div>
                  </div>
                </div>
              </div>

              {/* Shifts list */}
              <div className="space-y-3">
                {shifts.map((shift) => (
                  <div key={shift.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">{shift.title}</h4>
                          <StatusBadge
                            dimension="assignment"
                            status={shift.assignment.status}
                            label={statusLabel('assignment', shift.assignment.status)}
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>{formatDayDateToLocal(shift.start_time, 'dd/MM/yyyy')}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span>{formatTimeRangeToLocal(shift.start_time, shift.end_time)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span>{shift.location}</span>
                          </div>
                          {shift.event && (
                            <div className="flex items-center space-x-2">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span>{shift.event.title}</span>
                            </div>
                          )}
                        </div>

                        {shift.description && (
                          <p className="mt-2 text-sm text-gray-600">{shift.description}</p>
                        )}

                        {shift.assignment.notes && (
                          <div className="mt-2 p-2 bg-blue-50 rounded-md">
                            <p className="text-sm text-blue-800">
                              <strong>Notities:</strong> {shift.assignment.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VolunteerShiftsModal
